import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import styled, { keyframes } from 'styled-components';

import { type IExtendedUserDetails } from '@sine/auth-client';
import {
  Button,
  Flex,
  Grid,
  InlineLink,
  Modal,
  ProfileMenu,
  Text,
  colors,
  spacing,
  styleIf,
  useStylesFor,
  useStylesForBreakpoint,
} from '@sine/sine-ui-components';
import UICVersion from '@sine/sine-ui-components/version';

import apiVersionService from '../../external/apiVersion/apiVersion.service';
import { ROUTE_PATH } from '../../routing/routeConfig';
import rxEventHub, { RxEvent } from '../../services/events/rx';

const fadeIn = keyframes`
 0% { opacity: 0}
 100% { opacity: 1}
`;

export const OnboardingBackground = styled.div<{ showBackground: boolean }>`
  animation-name: ${fadeIn};
  animation-duration: 0.5s;
  height: 100%;
  width: 100%;
  background: ${colors.white};

  ${({ showBackground }) => styleIf(showBackground)`
    ${() => useStylesFor('desktop').andLarger`
        background-image: url('/img/background-onboarding.svg');
        background-position: right bottom;
        background-repeat: no-repeat;
    `}
  `}
`;

export const OnBoardingWrapper = styled(Flex)`
  top: 0;
  left: 0;
  overflow-y: auto;
  padding: ${spacing(4)} ${spacing(2)};
  height: 100%;
`;

export const OnBoardingContainer = styled(Flex)`
  ${() => useStylesFor('desktop').andLarger`
    max-width: 1200px;
  `}

  width: 100%;
  height: 100%;
`;

export const GridImageCard = styled(Grid)`
  display: none;

  ${() => useStylesForBreakpoint('xl')`
    display: block;
  `}
`;

const StyledContainer = styled.div<{ showBackground: boolean }>`
  ${({ showBackground }) => styleIf(showBackground)`
      padding: ${spacing(5)} 0 ${spacing(2)} 0;
  `}
`;

const TopBarContainer = styled(Flex)<{ showBackground: boolean }>`
  ${({ showBackground }) => styleIf(showBackground)`
  padding-bottom: ${spacing(13)};
  `}

  ${() => useStylesFor('tablet').andSmaller`
    padding-bottom: ${spacing(4.5)};
  `}

  position: relative;
  z-index: 1;
`;

const DemoButton = styled(Button)`
  ${() => useStylesFor('tablet').andSmaller`
    display: none;
  `}
`;

export const GridNameDetails = styled(Grid)`
  gap: 0;

  ${() => useStylesForBreakpoint('md')`
    gap: ${spacing(2)};
  `}
`;

export const Container = styled(Flex)`
  width: 100%;
`;

const PatentLink = styled(InlineLink)`
  font-size: 12px;
`;

interface PreLoginFrameProps {
  children: React.ReactNode;
  user?: IExtendedUserDetails;
  showBackground: boolean;
  showLoginLink?: boolean;
  showProfileMenu?: boolean;
}

export default function PreLoginFrame({ children, user, showBackground, showLoginLink = true }: PreLoginFrameProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [versionInfoModalOpen, setVersionInfoModalOpen] = useState(false);

  const history = useHistory();

  const handleLogout = () => {
    rxEventHub.broadcast(RxEvent.LOGOUT, {});
  };
  const handleNavigateProfile = () => {
    history.push(ROUTE_PATH.USER_SETTINGS);
  };

  const versions = [
    {
      name: 'core-api',
      value: apiVersionService.getApiVersion('core'),
    },
    {
      name: 'core-client',
      value: window.env.REACT_APP_CLIENT_VERSION,
    },
    {
      name: 'sine-ui-components',
      value: UICVersion,
    },
  ];

  return (
    <OnboardingBackground showBackground={showBackground} data-st-ext='background-onboarding'>
      <OnBoardingWrapper
        justifyContent='flex-start'
        direction='column'
        alignItems='center'
        data-st-ext='wrapper-onboarding'
      >
        <OnBoardingContainer
          direction='column'
          justifyContent='space-between'
          alignItems='stretch'
          data-st-ext='container-onboarding'
        >
          <TopBarContainer gap={2} justifyContent='space-between' alignItems='center' showBackground={showBackground}>
            <img src='/img/sine-by-honeywell-logo.png' />
            <Flex gap={3} alignItems='center'>
              {user ? (
                <>
                  <DemoButton
                    icon='sms'
                    variant='secondary'
                    size='lg'
                    onClick={() => (window.location.href = 'https://www.sine.co/demo/')}
                    data-st-ext='button-book-a-demo'
                  >
                    Book a demo
                  </DemoButton>

                  <ProfileMenu
                    photoURL={user.photoURL}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    open={profileMenuOpen}
                    placeholderStyle='initials'
                    data-st-ext='user-profile-menu'
                    isProvisioned={!user.verify.email.completed}
                    onMenuOpen={() => setProfileMenuOpen((prev) => !prev)}
                    onClickVersionInfo={() => {
                      setProfileMenuOpen(false);
                      setVersionInfoModalOpen(true);
                    }}
                  >
                    <ProfileMenu.Action onClick={handleNavigateProfile} data-st-ext='profile-menu-my-profile'>
                      My Profile
                    </ProfileMenu.Action>
                    <ProfileMenu.Action onClick={handleLogout} data-st-ext='profile-menu-logout'>
                      Log Out
                    </ProfileMenu.Action>
                  </ProfileMenu>

                  <Modal
                    header='Version info'
                    onClose={() => setVersionInfoModalOpen(false)}
                    size='sm'
                    show={versionInfoModalOpen}
                  >
                    {versions.map(({ name, value }) => (
                      <div key={name}>
                        {name}: {value}
                      </div>
                    ))}
                  </Modal>
                </>
              ) : (
                <>
                  {showLoginLink && (
                    <Text>
                      Have an account? <InlineLink href='/login'>Log in</InlineLink>
                    </Text>
                  )}
                  <DemoButton
                    icon='sms'
                    variant='secondary'
                    size='lg'
                    onClick={() => (window.location.href = 'https://www.sine.co/demo/')}
                    data-st-ext='button-book-a-demo'
                  >
                    Book a demo
                  </DemoButton>
                </>
              )}
            </Flex>
          </TopBarContainer>
          <Flex justifyContent='center' alignItems='center' grow={1}>
            {children}
          </Flex>

          <StyledContainer showBackground={showBackground}>
            <Flex direction='column'>
              <Text variant='small' colorVariant='text68' data-st-ext='footer-client-patent'>
                For an important patent notice, please visit:
                <PatentLink href='https://www.honeywell.com/us/en/patents'> www.honpat.com </PatentLink>
              </Text>
              <Text variant='small' colorVariant='text68' data-st-ext='footer-client-version'>
                Copyright. Sine Group Pty Ltd &copy; {new Date().getFullYear()} - {window.env.REACT_APP_CLIENT_VERSION}
              </Text>
            </Flex>
          </StyledContainer>
        </OnBoardingContainer>
      </OnBoardingWrapper>
    </OnboardingBackground>
  );
}
